import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import { DeleteOutline, Edit } from "@material-ui/icons";
import { Sparkles } from "lucide-react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import PromptModal from "../../components/PromptModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { hasStructuredPromptStack } from "../../utils/promptStackTemplate";
import { canAccessTalkAi } from "../../utils/userPermissions";
// import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  stackChip: {
    fontWeight: 600,
  },
  suggestionNotice: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newPrompts = [];

    prompts.forEach((prompt) => {
      const promptIndex = state.findIndex((p) => p.id === prompt.id);
      if (promptIndex !== -1) {
        state[promptIndex] = prompt;
      } else {
        newPrompts.push(prompt);
      }
    });

    return [...state, ...newPrompts];
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const promptIndex = state.findIndex((p) => p.id === prompt.id);

    if (promptIndex !== -1) {
      state[promptIndex] = prompt;
      return [...state];
    } else {
      return [prompt, ...state];
    }
  }

  if (action.type === "DELETE_PROMPT") {
    const promptId = action.payload;
    const promptIndex = state.findIndex((p) => p.id === promptId);
    if (promptIndex !== -1) {
      state.splice(promptIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Prompts = () => {
  const classes = useStyles();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasOpenAiPlan, setHasOpenAiPlan] = useState(null);
  const [messageSuggestionPromptId, setMessageSuggestionPromptId] =
    useState(null);
  const [
    updatingMessageSuggestionPromptId,
    setUpdatingMessageSuggestionPromptId,
  ] = useState(null);
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user?.companyId;
  const hasTalkAiAccess = canAccessTalkAi(user);

  useEffect(() => {
    async function fetchData() {
      if (!companyId) return;

      try {
        const planConfigs = await getPlanCompany(undefined, companyId);
        const planAllowsOpenAi = !!planConfigs?.plan?.useOpenAi;
        setHasOpenAiPlan(planAllowsOpenAi);

        if (!planAllowsOpenAi) {
          toast.error(
            "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
          );
          setTimeout(() => {
            history.push(`/`);
          }, 1000);
        }
      } catch (err) {
        setHasOpenAiPlan(false);
        toastError(err);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!hasTalkAiAccess || hasOpenAiPlan !== true) return;

    (async () => {
      setLoading(true);
      try {
        const [promptsResponse, suggestionConfigResponse] = await Promise.all([
          api.get("/prompt"),
          api.get("/prompt/message-suggestion"),
        ]);
        dispatch({
          type: "LOAD_PROMPTS",
          payload: promptsResponse.data.prompts,
        });
        setMessageSuggestionPromptId(
          suggestionConfigResponse.data.promptId || null
        );

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, [hasTalkAiAccess, hasOpenAiPlan]);

  useEffect(() => {
    if (!hasTalkAiAccess || hasOpenAiPlan !== true || !companyId) {
      return undefined;
    }

    // const socket = socketManager.GetSocket();

    const onPromptEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }

      if (data.action === "messageSuggestionConfig") {
        setMessageSuggestionPromptId(data.promptId || null);
      }
    };

    socket.on(`company-${companyId}-prompt`, onPromptEvent);
    return () => {
      socket.off(`company-${companyId}-prompt`, onPromptEvent);
    };
  }, [socket, companyId, hasTalkAiAccess, hasOpenAiPlan]);

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toast.info(i18n.t(data.message));
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  const handleUseForMessageSuggestion = async (prompt) => {
    setUpdatingMessageSuggestionPromptId(prompt.id);

    try {
      const { data } = await api.put("/prompt/message-suggestion", {
        promptId: prompt.id,
      });
      setMessageSuggestionPromptId(data.promptId);
      toast.success(
        "Este prompt será usado nas próximas sugestões de resposta."
      );
    } catch (err) {
      toastError(err);
    } finally {
      setUpdatingMessageSuggestionPromptId(null);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${
            selectedPrompt.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />
      {!hasTalkAiAccess || hasOpenAiPlan === false ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>{i18n.t("prompts.title")}</Title>
            <MainHeaderButtonsWrapper>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenPromptModal}
                style={{ borderRadius: "22px" }}
              >
                {i18n.t("prompts.buttons.add")}
              </Button>
            </MainHeaderButtonsWrapper>
          </MainHeader>
          <Paper className={classes.mainPaper} variant="outlined">
            <div className={classes.suggestionNotice}>
              <Sparkles size={19} aria-hidden="true" />
              <Typography variant="body2">
                Escolha o prompt usado para corrigir e melhorar mensagens. As
                alterações salvas nesse prompt valem já na próxima sugestão, sem
                reiniciar o sistema.
              </Typography>
            </div>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    {i18n.t("prompts.table.name")}
                  </TableCell>
                  <TableCell align="left">
                    {i18n.t("prompts.table.queue")}
                  </TableCell>
                  <TableCell align="left">
                    {i18n.t("prompts.table.max_tokens")}
                  </TableCell>
                  <TableCell align="left">Arquitetura</TableCell>
                  <TableCell align="left">Uso</TableCell>
                  <TableCell align="center">
                    {i18n.t("prompts.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {prompts.map((prompt) => (
                    <TableRow key={prompt.id}>
                      <TableCell align="left">{prompt.name}</TableCell>
                      <TableCell align="left">{prompt.queue.name}</TableCell>
                      <TableCell align="left">{prompt.maxTokens}</TableCell>
                      <TableCell align="left">
                        {hasStructuredPromptStack(prompt.prompt) ? (
                          <Chip
                            size="small"
                            color="primary"
                            label="Estruturado"
                            className={classes.stackChip}
                          />
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="Legado"
                            className={classes.stackChip}
                          />
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {messageSuggestionPromptId === prompt.id ? (
                          <Chip
                            size="small"
                            color="primary"
                            label="Sugestão de respostas"
                            className={classes.stackChip}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            messageSuggestionPromptId === prompt.id
                              ? "Este prompt já está em uso nas sugestões"
                              : "Usar nas sugestões de resposta"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color={
                                messageSuggestionPromptId === prompt.id
                                  ? "primary"
                                  : "default"
                              }
                              disabled={
                                messageSuggestionPromptId === prompt.id ||
                                updatingMessageSuggestionPromptId !== null
                              }
                              aria-label="Usar nas sugestões de resposta"
                              onClick={() =>
                                handleUseForMessageSuggestion(prompt)
                              }
                            >
                              <Sparkles size={19} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => handleEditPrompt(prompt)}
                        >
                          <Edit />
                        </IconButton>

                        <Tooltip
                          title={
                            messageSuggestionPromptId === prompt.id
                              ? "Selecione outro prompt antes de excluir este"
                              : "Excluir prompt"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={messageSuggestionPromptId === prompt.id}
                              onClick={() => {
                                setSelectedPrompt(prompt);
                                setConfirmModalOpen(true);
                              }}
                            >
                              <DeleteOutline />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={6} />}
                </>
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Prompts;
